import { i18n } from "@/boot/i18n";
import { SURVEY_STATUS } from "@/consts/survey";
import { ACTIVATION, VISUALIZATION, SHOW_WIDGET, EXHIBITION_MODES } from "@/consts/widget";
import { MIN_LENGTH, REQUIRED } from "../validator/methods";
import WidgetService from "../../../services/widget";
import { getWidgetDefaultState } from "./widgetState";
import ErrorHandler from "@/tools/errorHandler";
import * as types from "./mutationTypes";

export default {
  initListState({ commit }) {
    commit(types.SET_WIDGETS, []);
  },
  clearListState({ commit }) {
    commit(types.CLEAR_WIDGETS);
  },
  async initNewState({ commit, dispatch }) {
    commit(types.SET_WIDGET, getWidgetDefaultState());
    const pagination = {
      "filter.status.equal": [SURVEY_STATUS.FINISHED, SURVEY_STATUS.DISPATCHED]
    };
    await dispatch("survey/getAllTree", pagination, { root: true });
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.deactivateResponsesValidator, { root: true });
    addHostValidation(dispatch, 0);
  },
  clearNewState({ commit, dispatch }) {
    commit(types.CLEAR_WIDGET);
    dispatch("validator/clearValidator", null, { root: true });
    dispatch("survey/clearSurveys", null, { root: true });
    dispatch("metadata/clearMetadatas", null, { root: true });
  },
  setWidgetSuccess({ commit }, widget) {
    commit(types.SET_WIDGET, widget);
  },
  setWidgetsSuccess({ commit }, widgets) {
    commit(types.SET_WIDGETS, widgets);
  },
  async getAll({ rootState, dispatch }) {
    try {
      const response = await WidgetService.getAll({ organizationUUID: rootState.organization.organization.uuid });
      if (response.data.data) {
        dispatch("setWidgetsSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOne({ rootState, dispatch }, uuid) {
    const response = await WidgetService.getOne({
      organizationUUID: rootState.organization.organization.uuid,
      widgetUUID: uuid
    });
    if (response.data.data) {
      dispatch("setWidgetSuccess", response.data.data);
    }
  },
  create({ rootState }, widget) {
    return WidgetService.create({ organizationUUID: rootState.organization.organization.uuid, widget });
  },
  update({ rootState }, widget) {
    return WidgetService.update({ organizationUUID: rootState.organization.organization.uuid, widget });
  },
  async save({ state, dispatch }) {
    const isValid = await dispatch("validate");
    if (!isValid) {
      return false;
    }
    try {
      let widget = state.widget;

      // remove data that is not handled in backend
      delete widget.activation;
      delete widget.deactivateByResponses;
      delete widget.deactivateByDate;
      delete widget.visualization;

      let widgetSaved = null;
      if (widget.uuid) {
        const response = await dispatch("update", widget);
        widgetSaved = response.data.data;
      } else {
        const response = await dispatch("create", widget);
        widgetSaved = response.data.data;
      }
      dispatch("setWidgetSuccess", widgetSaved);
      dispatch("ui/showToastSuccess", i18n.t("widget.messages.save_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
    return true;
  },
  async getHosts({ rootState, commit, dispatch }, uuid) {
    try {
      const response = await WidgetService.getHosts({
        organizationUUID: rootState.organization.organization.uuid,
        widgetUUID: uuid
      });
      if (response.data.data) {
        commit(types.SET_HOSTS, response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  setEnabled({ commit }, enabled) {
    commit(types.SET_ENABLED, enabled);
  },
  setName({ state, commit, dispatch }, name) {
    dispatch("validator/checkValidation", { field: "name", value: state.widget.name }, { root: true });
    commit(types.SET_NAME, name);
  },
  addHost({ commit, state, dispatch }) {
    commit(types.ADD_HOST);
    addHostValidation(dispatch, state.widget.hosts.length - 1);
  },
  removeHost({ commit, dispatch }, index) {
    dispatch("validator/removeError", "host_name" + index, { root: true });
    dispatch("validator/removeError", "survey_uuid" + index, { root: true });

    commit(types.REMOVE_HOST, index);
  },
  setHostPath({ commit, dispatch }, { value, index }) {
    dispatch("validator/checkValidation", { field: "host_name" + index, value: value }, { root: true });
    commit(types.SET_HOST_PATH, { value, index });
  },
  setHostSurvey({ commit, rootState, dispatch }, { value, index }) {
    dispatch("validator/checkValidation", { field: "survey_uuid" + index, value: value }, { root: true });
    if (index === 0) {
      const survey = rootState.survey.surveys.find((s) => s.uuid === value);
      commit(types.SET_SURVEY, survey);
    }
    commit(types.SET_HOST_SURVEY, { value, index });
  },
  setManualDeactivation({ commit }) {
    commit(types.SET_MANUAL);
  },
  setResponsesToDeactivate({ commit }, responses) {
    commit(types.SET_RESPONSES, responses);
  },
  setActivation({ commit, dispatch }, value) {
    if (value === ACTIVATION.MANUAL) {
      dispatch("setDeactivateByResponses", false);
      dispatch("setDeactivateByDate", false);
    }
    commit(types.SET_ACTIVATION, value);
  },
  setDeactivateByResponses({ commit, dispatch }, value) {
    if (!value) {
      dispatch("clearResponses", false);
    }
    commit(types.SET_DEACTIVATION_RESPONSES, value);
  },
  setDeactivateByDate({ commit, dispatch }, value) {
    if (!value) {
      dispatch("clearDates");
    }
    commit(types.SET_DEACTIVATION_DATE, value);
  },
  setActivationDate({ commit }, date) {
    commit(types.SET_START_DATE, date);
  },
  setDeactivationDate({ commit }, date) {
    commit(types.SET_END_DATE, date);
  },
  clearResponses({ dispatch, commit }) {
    commit(types.SET_RESPONSES, 0);
    dispatch("validator/removeError", "responses", { root: true });
  },
  clearDates({ commit }) {
    commit(types.SET_START_DATE, null);
    commit(types.SET_END_DATE, null);
  },
  setVisualization({ commit, dispatch }, value) {
    if (value === VISUALIZATION.IMMEDIATE) {
      dispatch("setDelay", 0);
    }
    commit(types.SET_VISUALIZATION, value);
  },
  setDelay({ commit }, value) {
    commit(types.SET_DELAY, Number(value));
  },
  setRecurrence({ commit }, value) {
    if (value !== SHOW_WIDGET.RECURRENTLY) {
      commit(types.SET_RECURRENCE_DAYS_RESPONDED, 0);
      commit(types.SET_RECURRENCE_DAYS_REFUSED, 0);
    }
    commit(types.SET_RECURRENCE, value);
  },
  setRecurrenceDaysResponded({ commit }, value) {
    commit(types.SET_RECURRENCE_DAYS_RESPONDED, value);
  },
  setRecurrenceDaysRefused({ commit }, value) {
    commit(types.SET_RECURRENCE_DAYS_REFUSED, value);
  },
  setDesignExhibition({ commit, dispatch }, value) {
    if (value === EXHIBITION_MODES.EMBEDDED) {
      dispatch("setDesignMinimized", false);
    }
    commit(types.SET_EXHIBITION, value);
  },
  setDesignMinimized({ commit }, value) {
    commit(types.SET_MINIMIZED, value);
  },
  setDesignSurveyColor({ commit }, value) {
    commit(types.SET_SURVEY_COLOR, value);
  },
  setDesignPrimaryColor({ commit }, value) {
    commit(types.SET_PRIMARY_COLOR, value);
  },
  setDesignSecondaryColor({ commit }, value) {
    commit(types.SET_SECONDARY_COLOR, value);
  },
  setDesignThanksMessage({ commit }, { language, value }) {
    commit(types.SET_THANKS_MESSAGE, { language, value });
  },
  validate({ dispatch, rootState }) {
    dispatch("validateName");
    dispatch("validateURLList");
    dispatch("validateDeactivateByResponses");
    return rootState.validator.errors?.length === 0;
  },
  validateName({ state, dispatch }) {
    dispatch("validator/validate", { field: "name", value: state.widget.name }, { root: true });
  },
  validateURLList({ state, dispatch }) {
    state.widget.hosts.forEach((u, index) => {
      dispatch("validator/validate", { field: "host_name" + index, value: u.host_name }, { root: true });
      dispatch("validator/validate", { field: "survey_uuid" + index, value: u.survey_uuid }, { root: true });
    });
  },
  validateDeactivateByResponses({ state, dispatch }) {
    const value = { deactivateByResponses: state.widget.deactivateByResponses, responses: state.widget.responses };
    dispatch("validator/validate", { field: "responses", value }, { root: true });
  }
};

const validators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [{ method: MIN_LENGTH, value: 4, message: i18n.t("widget.messages.name_error") }],
      meta: { tabIndex: 0 }
    },
    deactivateResponsesValidator: {
      field: "responses",
      validations: [
        {
          method: deactivateResponsesCustomMethod,
          value: 1,
          message: i18n.t("widget.messages.zero_negative_value")
        }
      ]
    }
  };
};

function deactivateResponsesCustomMethod({ deactivateByResponses, responses }) {
  if (deactivateByResponses && !responses > 0) {
    return false;
  }
  return true;
}

function addHostValidation(dispatch, index) {
  const pathValidator = {
    field: "host_name" + index,
    validations: [{ method: REQUIRED, message: i18n.t("widget.messages.url_name_error") }],
    meta: { tabIndex: 0 }
  };
  dispatch("validator/addValidator", pathValidator, { root: true });
  const surveyValidator = {
    field: "survey_uuid" + index,
    validations: [{ method: REQUIRED, message: i18n.t("widget.messages.url_no_survey") }],
    meta: { tabIndex: 0 }
  };
  dispatch("validator/addValidator", surveyValidator, { root: true });
}
